import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertCircle, Building2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RecoverPage() {
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!key) {
      setStatus('error');
      setMessage('Missing recovery key. Visit /login/recover?key=YOUR_KEY');
      return;
    }
    (async () => {
      try {
        // Nuke any stale auth state first
        try {
          localStorage.removeItem('auth_token');
          sessionStorage.clear();
          document.cookie.split(';').forEach((c) => {
            const name = c.split('=')[0].trim();
            if (name) document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          });
        } catch { /* no-op */ }

        const res = await axios.get(`${API_URL}/api/auth/recovery`, { params: { key } });
        const data = res.data;
        localStorage.setItem('auth_token', data.access_token);
        if (typeof setUser === 'function') {
          setUser({
            id: data.id,
            username: data.username,
            name: data.name,
            role: data.role,
            praxis_client_id: data.praxis_client_id,
          });
        }
        setStatus('success');
        setMessage(`Welcome back, ${data.name}. Redirecting...`);
        setTimeout(() => navigate('/home', { replace: true }), 1000);
      } catch (err) {
        setStatus('error');
        const detail = err?.response?.data?.detail || err.message;
        setMessage(typeof detail === 'string' ? detail : 'Recovery failed');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F5F2] px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="recover-page">
        <div className="w-12 h-12 mx-auto bg-gray-900 rounded-xl flex items-center justify-center mb-4">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <h1 className="text-xl font-semibold mb-2">Admin Recovery</h1>
        {status === 'loading' && (
          <div className="flex flex-col items-center gap-2 py-6 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p>Signing you in...</p>
          </div>
        )}
        {status === 'success' && (
          <div className="flex flex-col items-center gap-2 py-6 text-emerald-600">
            <CheckCircle2 className="w-8 h-8" />
            <p className="text-gray-700">{message}</p>
          </div>
        )}
        {status === 'error' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <AlertCircle className="w-8 h-8 text-red-500" />
            <p className="text-red-700">{message}</p>
            <button onClick={() => navigate('/login')} className="text-sm text-blue-600 hover:underline mt-2">
              Back to login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
